import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types';
import { authStateManager } from '@/services/authStateManager';
import { getRequiredPermissionForPath, hasPermission } from '@/utils/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: UserRole[];
  fallback?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  roles = [],
  fallback = <div>Loading...</div>,
}) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();
  const location = useLocation();

  // Check if auth state manager is currently redirecting
  useEffect(() => {
    if (authStateManager.isCurrentlyRedirecting()) {
      return;
    }
  }, []);

  // Show loading state
  if (isLoading) {
    return <>{fallback}</>;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    // Only redirect if not already redirecting
    if (!authStateManager.isCurrentlyRedirecting()) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <>{fallback}</>;
  }

  // Check role-based access
  if (roles.length > 0 && user.role && !roles.includes(user.role as UserRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  const requiredPermission = getRequiredPermissionForPath(location.pathname);
  if (!hasPermission(user, requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};
